import React from "react";

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Capture the error and errorInfo to display and optionally send to monitoring
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      // Render custom fallback UI including the error information
      return (
        <div>
          <h1>Something went wrong. Please try again later.</h1>
          <div>
            <p>
              You can give the following information to your AI architect in
              chat to help them resolve the problem for you:
            </p>
            <details style={{ whiteSpace: "pre-wrap" }}>
              <summary>Click for error details</summary>
              <p>
                <strong>Error:</strong>{" "}
                {this.state.error && this.state.error.toString()}
              </p>
              <p>
                <strong>Stack Trace:</strong>{" "}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </p>
            </details>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
